import { useState } from 'react';
import { Select } from '@ardoq/select';
import { CommonFieldProps } from '@ardoq/forms';
import { IconName } from '@ardoq/icons';
import { getCurrentLocale } from '@ardoq/locale';
import { SELECT_DATE } from './const';
import { RelativeDateInput } from './RelativeDateInput';
import { noop } from 'lodash';
import {
  Mode,
  RelativeDatePickerMode,
  RelativeDatePickerValue,
  RelativeDatePresetMode,
} from './types';
import { useIsMounted } from '@ardoq/hooks';
import {
  composeRelativeDate,
  getDefaultOptions,
  getInputModeFromValue,
  getIsDefaultModeValue,
  getIsISODateString,
  isRelativeDate,
  isTimeFrameUnit,
} from './utils';
import {
  formatDateShort,
  formatDateISO,
  parseLocaleDateString,
  TODAY_VALUE,
} from '@ardoq/date-time';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';
import { DateTimePicker } from '../DateTimePicker';

type RelativeDatePickerProps = CommonFieldProps & {
  placeholder?: string;
  onChange: (value: RelativeDatePickerValue) => void;
  value: RelativeDatePickerValue;
  clickId?: string;
  relativeDatePickerMode?: RelativeDatePickerMode;
  presetMode?: RelativeDatePresetMode;
  /** Show calendar icon on the left side of the input */
  showIcon?: boolean;
  menuPortalTarget?: HTMLElement | null;
  i18nLocale?: I18nLocale;
};

export const RelativeDatePicker = ({
  placeholder = 'Select...',
  onChange = noop,
  value,
  relativeDatePickerMode = RelativeDatePickerMode.DATE_TIME,
  clickId,
  presetMode = RelativeDatePresetMode.NORMAL,
  showIcon = false,
  menuPortalTarget,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
  ...commonFieldProps
}: RelativeDatePickerProps) => {
  // in case the initial mode is RELATIVE_DATE we don't want to open it
  // onMount
  const isMounted = useIsMounted();
  const [mode, setMode] = useState<Mode>(
    getInputModeFromValue(value, presetMode)
  );
  const locale = getCurrentLocale();

  const handleOnChange = (selectedValue: RelativeDatePickerValue) => {
    if (!selectedValue) {
      setMode(Mode.DEFAULT);
    } else if (selectedValue === SELECT_DATE) {
      setMode(Mode.DATE_PICKER);
    } else if (
      selectedValue !== TODAY_VALUE &&
      getIsDefaultModeValue(selectedValue, presetMode) &&
      isTimeFrameUnit(selectedValue)
    ) {
      setMode(Mode.RELATIVE_DATE);
      onChange(composeRelativeDate({ unit: selectedValue, value: 1 }));
    } else if (
      selectedValue === TODAY_VALUE ||
      getIsISODateString(selectedValue)
    ) {
      setMode(Mode.DEFAULT);
      onChange(selectedValue);
    } else if (
      getIsDefaultModeValue(selectedValue, presetMode) &&
      !isTimeFrameUnit(selectedValue)
    ) {
      // Handle Tomorrow/Yesterday in SHORT mode - keep in DEFAULT mode
      setMode(Mode.DEFAULT);
      onChange(selectedValue);
    } else if (isRelativeDate(selectedValue)) {
      onChange(selectedValue);
    }
  };

  return (
    <>
      {mode === Mode.DEFAULT && (
        <Select
          aria-label={
            commonFieldProps.label ??
            t(i18nLocale, 'dateTimePicker:selectDate', 'Select date')
          }
          value={value}
          onValueChange={selectedValue =>
            handleOnChange((selectedValue || undefined) as string | undefined)
          }
          placeholder={placeholder}
          options={getDefaultOptions(
            value,
            relativeDatePickerMode,
            presetMode,
            i18nLocale
          )}
          {...commonFieldProps}
          clickId={clickId}
          hasWarning={commonFieldProps?.hasWarning}
          iconName={showIcon ? IconName.CALENDAR : undefined}
          menuPortalTarget={menuPortalTarget}
        />
      )}
      {mode === Mode.RELATIVE_DATE && (
        <RelativeDateInput
          value={value}
          onChange={handleOnChange}
          relativeDatePickerMode={relativeDatePickerMode}
          autoFocus={isMounted}
          menuPortalTarget={menuPortalTarget}
          i18nLocale={i18nLocale}
          {...commonFieldProps}
        />
      )}
      {mode === Mode.DATE_PICKER && (
        <DateTimePicker
          date={parseLocaleDateString(value || '', locale)}
          onCloseDatepicker={() => setMode(Mode.DEFAULT)}
          onChange={date => onChange(formatDateISO(date) || undefined)}
          config={{ formatter: formatDateShort, locale: locale }}
          placeholder={placeholder}
          autoFocus
          hideIcon
          i18nLocale={i18nLocale}
          {...commonFieldProps}
        />
      )}
    </>
  );
};
